import { Link, useParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CmsPage from "@/components/cms/CmsPage";
import EnquiryForm from "@/components/feature/EnquiryForm";
import { getVillaModel, VILLA_MODELS } from "@/pages/home/villaModels";
import { DESTINATION_NAMES } from "@/pages/public/destinations/data";

const SPEC_ITEMS = (v: { sqft: string; bedrooms: string; guests: string }) => [
  { icon: "ri-ruler-line", label: "Built-Up Area", value: v.sqft },
  { icon: "ri-hotel-bed-line", label: "Configuration", value: v.bedrooms },
  { icon: "ri-user-smile-line", label: "Sleeps", value: v.guests },
];

export default function VillaDetail() {
  const { slug } = useParams<{ slug: string }>();
  const villa = getVillaModel(slug || "");

  if (!villa) {
    return (
      <main className="w-full">
        <Navbar />
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto py-24 text-center">
          <i className="ri-home-4-line text-5xl text-foreground-400"></i>
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-4">Villa not found</h1>
          <p className="text-sm text-foreground-600 mt-2">This villa model may have moved or is no longer listed.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-2.5 hover:bg-primary-700">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const others = VILLA_MODELS.filter((v) => v.slug !== villa.slug);

  return (
    <main className="w-full">
      <Navbar />
      <CmsPage
        slug={`villa-${villa.slug}`}
        path={`/villa/${villa.slug}`}
        fallbackSeo={{
          title: `${villa.name} ${villa.type} Villa | NOMADLIVINGS`,
          description: `Invest in the ${villa.name} ${villa.type} luxury A-frame villa at Ridge Top Estates, Lonavala — starting at ${villa.priceLabel} with an estimated ${villa.roi} annual return. ${villa.description}`,
        }}
      >
        {/* Hero */}
        <section className="relative w-full">
          <div className="relative w-full h-[380px] md:h-[560px] overflow-hidden">
            <img
              src={villa.gallery[0]}
              alt={`${villa.name} ${villa.type} villa hero`}
              title={`${villa.name} villa - ${villa.type}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/20"></div>
            <div className="absolute inset-x-0 bottom-0 w-full">
              <div className="w-full px-4 md:px-8 max-w-7xl mx-auto pb-8 md:pb-12">
                <nav className="flex items-center gap-2 text-xs md:text-sm text-background-100/80 flex-wrap">
                  <Link to="/" className="hover:text-background-50 whitespace-nowrap">Home</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to="/investments" className="hover:text-background-50 whitespace-nowrap">Investments</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-background-50 whitespace-nowrap">{villa.name}</span>
                </nav>
                <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-background-50/90 text-primary-700 px-4 py-1.5 text-xs font-semibold">
                      <i className="ri-vip-diamond-line"></i> {villa.type}
                    </span>
                    <h1 className="font-heading text-3xl md:text-5xl text-background-50 mt-3 leading-tight">
                      {villa.name} <span className="italic text-accent-300">Villa</span>
                    </h1>
                    <p className="text-background-100/90 text-sm md:text-base mt-2">{villa.tag} model · Est. ROI {villa.roi} per year</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[11px] uppercase tracking-wide text-background-100/70">Starting from</p>
                      <p className="font-heading text-2xl md:text-3xl font-extrabold text-background-50">{villa.priceLabel}</p>
                    </div>
                    <a
                      href="tel:8088330330"
                      className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-5 py-3 hover:bg-accent-600 transition-colors"
                    >
                      <i className="ri-whatsapp-line"></i> Enquire
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specs strip */}
        <section className="w-full py-10 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            {SPEC_ITEMS(villa).map((s) => (
              <div key={s.label} className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-5">
                <span className="inline-flex items-center justify-center w-12 h-12 shrink-0 rounded-full bg-accent-100 text-accent-700">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-600">{s.label}</p>
                  <p className="font-heading text-xl text-foreground-950 mt-0.5">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Overview + gallery */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-start">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
                <i className="ri-home-4-line"></i> Overview
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 leading-tight">
                About the <span className="italic text-primary-700">{villa.name}</span>
              </h2>
              <p className="text-foreground-600 text-sm md:text-base mt-4 leading-relaxed">{villa.longDescription}</p>

              <ul className="mt-6 space-y-3">
                {villa.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-foreground-700">
                    <span className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-full bg-primary-100 text-primary-700">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-8 rounded-2xl bg-accent-50 border border-accent-200 p-5 md:p-6">
                <p className="text-xs uppercase tracking-wide text-foreground-600 font-semibold">Key highlights</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {villa.featured.map((h) => (
                    <span key={h} className="inline-flex items-center gap-1.5 rounded-full bg-background-50 border border-accent-200 px-3 py-1.5 text-xs font-medium text-foreground-800">
                      <i className="ri-star-smile-line text-accent-600"></i> {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="col-span-2 rounded-2xl overflow-hidden border border-background-200">
                <img src={villa.gallery[1]} alt={`${villa.name} living room`} className="w-full h-52 md:h-64 object-cover" />
              </div>
              {villa.gallery.slice(2).map((img, i) => (
                <div key={i} className="rounded-2xl overflow-hidden border border-background-200">
                  <img src={img} alt={`${villa.name} interior ${i + 1}`} className="w-full h-40 md:h-48 object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enquiry */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-accent-600">
                <i className="ri-mail-send-line"></i> Get Started
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 leading-tight">
                Enquire About the <span className="italic text-primary-700">{villa.name}</span>
              </h2>
              <p className="text-foreground-600 text-sm md:text-base mt-4 leading-relaxed">
                Tell us you&apos;re interested in the {villa.name} model and our investment advisors
                will share pricing, projected returns and site-visit options tailored to you.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="tel:8088330330" className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700 transition-colors">
                  <i className="ri-phone-fill"></i> Call 80883 30330
                </a>
                <a href="tel:8088330330" className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-primary-600 text-primary-700 px-6 py-3 hover:bg-primary-600 hover:text-background-50 transition-colors">
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
              </div>
            </div>
            <div className="rounded-3xl bg-background-50 border border-background-200 p-5 md:p-8">
              <EnquiryForm
                page={`villa-${villa.slug}`}
                title={`${villa.name} Enquiry`}
                subtitle="No obligation. A dedicated advisor will guide you through pricing, returns and the purchase journey."
                destinationOptions={DESTINATION_NAMES}
                defaultInterest={`${villa.name} ${villa.type} Villa`}
                ctaLabel="Send Villa Enquiry"
              />
            </div>
          </div>
        </section>

        {/* Other villas */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
                  <i className="ri-swap-line"></i> Compare
                </span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 leading-tight">
                  Explore Other <span className="italic text-primary-700">Villa Models</span>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-10">
              {others.map((v) => (
                <Link
                  key={v.id}
                  to={`/villa/${v.slug}`}
                  className="group relative flex flex-col overflow-hidden rounded-2xl bg-background-100 border border-background-200 hover:border-primary-300 transition-all"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={v.gallery[0]}
                      alt={`${v.name} ${v.type} villa`}
                      title={`${v.name} villa - ${v.type}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary-700 bg-primary-100 rounded-full px-3 py-1">{v.type}</span>
                      <span className="text-[10px] font-bold uppercase text-accent-700 bg-accent-100 rounded-full px-2.5 py-1">{v.tag}</span>
                    </div>
                    <h3 className="font-heading text-xl text-foreground-950 mt-4">{v.name}</h3>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-lg font-extrabold text-primary-700">{v.priceLabel}</p>
                      <p className="text-sm font-bold text-accent-600">{v.roi} ROI</p>
                    </div>
                    <span className="mt-5 inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2.5 group-hover:bg-primary-700 transition-colors">
                      View Details <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </CmsPage>
      <Footer />
    </main>
  );
}